import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { useAuthListener } from './hooks/useAuth';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import { InactivityWarningBanner } from './components/ui/InactivityWarningBanner';
import { useFocusOnNavigate } from './hooks/useFocusOnNavigate';
import { usePageView } from './hooks/usePageView';
import { useAuthStore } from './store/authStore';
import { useChildStore } from './store/childStore';
import { useSettingsStore } from './store/settingsStore';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AppLayout } from './components/layout/AppLayout';

// Critical path — static imports (needed immediately)
import Login from './pages/Login';
import ChildHome from './pages/ChildHome';

// All other pages — lazy loaded for code splitting
const Landing = lazy(() => import('./pages/Landing'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const VerkstedetPage = lazy(() => import('./pages/VerkstedetPage'));
const ChildProfile = lazy(() => import('./pages/onboarding/ChildProfile'));
const PathwaySuggestion = lazy(() => import('./pages/onboarding/PathwaySuggestion'));
const UpgradeSuccess = lazy(() => import('./pages/parent/UpgradeSuccess'));
const LessonExperience = lazy(() => import('./pages/lesson/LessonExperience'));
const LessonComplete = lazy(() => import('./pages/lesson/LessonComplete'));
const WorksheetDownload = lazy(() => import('./pages/lesson/WorksheetDownload'));
const SubjectLibrary = lazy(() => import('./pages/SubjectLibrary'));
const ParentDashboard = lazy(() => import('./pages/parent/ParentDashboard'));
const ParentSettings = lazy(() => import('./pages/parent/ParentSettings'));
const PrintPacks = lazy(() => import('./pages/parent/PrintPacks'));
const UpgradePage = lazy(() => import('./pages/parent/UpgradePage'));
const GamePage = lazy(() => import('./pages/game/GamePage'));
const BlogListing = lazy(() => import('./pages/blog/BlogListing'));
const BlogPostPage = lazy(() => import('./pages/blog/BlogPostPage'));
const IgOauthCallback = lazy(() => import('./pages/IgOauthCallback'));

/**
 * Legacy /blog/:slug → /undring/:slug.
 *
 * `<Navigate to="/undring/:slug" />` does NOT interpolate route params — it
 * navigates to the literal string ":slug", which resolves to a post lookup for
 * ":slug", misses, and renders "Artikkel ikke funnet". Every inbound link to the
 * old /blog/ path was landing on that dead end. Read the param and build the
 * real target instead.
 */
function LegacyBlogRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={slug ? `/undring/${slug}` : '/undring'} replace />;
}

// Guards
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore();
  if (loading) return <LoadingSpinner fullScreen message="Laster Good Atoms..." />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireChild({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore();
  const { activeChild } = useChildStore();
  if (loading) return <LoadingSpinner fullScreen message="Laster Good Atoms..." />;
  if (!user) return <Navigate to="/login" replace />;
  if (!activeChild) return <Navigate to="/onboarding/child" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  useAuthListener();
  const { warningVisible, minutesLeft, stayLoggedIn } = useInactivityTimeout();
  useFocusOnNavigate();
  usePageView();
  const { loading } = useAuthStore();

  if (loading) return <LoadingSpinner fullScreen message="Laster Good Atoms..." />;

  return (
    <>
      {warningVisible && (
        <InactivityWarningBanner minutesLeft={minutesLeft} onStayLoggedIn={stayLoggedIn} />
      )}
      <Suspense fallback={<LoadingSpinner fullScreen message="Laster..." />}>
        <Routes>
        {/* Public */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/verkstedet" element={<VerkstedetPage />} />
        <Route path="/undring" element={<BlogListing />} />
        <Route path="/undring/:slug" element={<BlogPostPage />} />
        {/* Legacy redirect */}
        <Route path="/blog" element={<Navigate to="/undring" replace />} />
        <Route path="/blog/:slug" element={<LegacyBlogRedirect />} />
        <Route path="/upgrade-success" element={<UpgradeSuccess />} />
        {/* Internal: Instagram OAuth callback (admin token setup) */}
        <Route path="/oauth/ig-callback" element={<IgOauthCallback />} />

        {/* Onboarding */}
        <Route
          path="/onboarding/child"
          element={
            <RequireAuth>
              <ChildProfile />
            </RequireAuth>
          }
        />
        <Route
          path="/onboarding/pathway"
          element={
            <RequireAuth>
              <PathwaySuggestion />
            </RequireAuth>
          }
        />

        {/* App shell — authenticated + child required */}
        <Route
          element={
            <RequireChild>
              <AppLayout />
            </RequireChild>
          }
        >
          <Route path="/home" element={<ChildHome />} />
          <Route path="/library" element={<SubjectLibrary />} />
          <Route path="/lesson/:lessonId" element={<LessonExperience />} />
          <Route path="/lesson/:lessonId/complete" element={<LessonComplete />} />
          <Route path="/lesson/:lessonId/worksheet" element={<WorksheetDownload />} />
          <Route path="/game/:gameId" element={<GamePage />} />
          <Route path="/parent" element={<ParentDashboard />} />
          <Route path="/parent/settings" element={<ParentSettings />} />
          <Route path="/parent/printpacks" element={<PrintPacks />} />
        </Route>

        {/* Upgrade — requires auth only, no child needed */}
        <Route
          path="/account/subscription"
          element={
            <RequireAuth>
              <UpgradePage />
            </RequireAuth>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </>
  );
}

export default function App() {
  const { reduceMotion } = useSettingsStore();
  return (
    <ErrorBoundary>
      <MotionConfig reducedMotion={reduceMotion ? 'always' : 'user'}>
        <BrowserRouter>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </BrowserRouter>
      </MotionConfig>
    </ErrorBoundary>
  );
}
